if (!customElements.get('free-gift')) {
  customElements.define(
    'free-gift',
    class FreeGift extends HTMLElement {
      constructor() {
        super();
        this.cartTotal = Number(this.dataset.cartTotal) || 0;
        this.freeGiftThreshold = Number(this.dataset.freeGiftThreshold) || 0;
        this.message = this.dataset.message || '';
        this.messageSomeAmmount = this.dataset.messageSomeAmmount || '';
        this.messageCompleted = this.dataset.messageCompleted || '';
        this.messageAddingGift = this.dataset.messageAddingGift || 'Adding free gift...';
        this.messageReAddButton = this.dataset.messageReAddButton || 'Add Gift';
        this.messageSubscription = this.dataset.messageSubscription || '';
        this.compiledMessage = '';
        this.currencySymbol = this.dataset.currencySymbol || '';
        this.roundAmount = this.dataset.roundAmount === 'true';
        this.observer = new MutationObserver(this.handleMutations.bind(this));
        this.progressBar = this.querySelector('progress-bar');
        this.hasGift = false; // Track if gift is in cart
      }

      connectedCallback() {
        this.setMessage(this.getAmountLeft(this.cartTotal));
        this.addMessage();
        this.addEventListeners();
        this.observeAttributes();
        this.updateOtherFreeGiftElements();
        this.attachButtonListener();
      }

      attachButtonListener() {
        // Attach click listener to the re-add button
        const reAddBtn = this.querySelector('#freeGiftReAddBtn');
        if (reAddBtn) {
          // Remove any existing listeners first
          const newBtn = reAddBtn.cloneNode(true);
          reAddBtn.parentNode.replaceChild(newBtn, reAddBtn);

          newBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleReAddGift();
          });
        }
      }

      disconnectedCallback() {
        this.removeEventListeners();
        this.observer.disconnect();
      }

      addEventListeners() {
        this.addEventListener('updateFreeGift', this.updateFreeGift.bind(this));
        this.addEventListener('reAddGift', this.handleReAddGift.bind(this));
      }

      removeEventListeners() {
        this.removeEventListener('updateFreeGift', this.updateFreeGift.bind(this));
      }

      observeAttributes() {
        this.observer.observe(this, { attributes: true, attributeFilter: ['data-cart-total'] });
      }

      handleMutations(mutations) {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-cart-total') {
            this.cartTotal = this.dataset.cartTotal;
            this.updateContent();
          }
        }
      }

      getAmountLeft(total) {
        const currencyRate = 1; // Shopify.currency.rate;
        let amountLeftToSpend = (this.freeGiftThreshold - total * currencyRate) / 100;
        amountLeftToSpend = Math.round(amountLeftToSpend * 100) / 100;
        amountLeftToSpend = this.roundAmount ? Math.round(amountLeftToSpend) : amountLeftToSpend;
        return amountLeftToSpend <= 0 ? 0 : this.currencySymbol + amountLeftToSpend;
      }

      setMessage(amountLeftToSpend) {
        if (this.cartTotal === 0) {
          this.compiledMessage = this.message;
        } else if (this.cartTotal >= this.freeGiftThreshold) {
          // Eligible for gift - message only, button visibility handled by updateButtonVisibility()
          this.compiledMessage = this.messageCompleted;
        } else {
          this.compiledMessage = this.messageSomeAmmount.replace('{{ amount }}', amountLeftToSpend);
        }
      }

      addMessage() {
        const node = document.createElement('p');
        node.innerHTML = this.compiledMessage;
        this.prepend(node);
      }

      updateContent() {
        this.setMessage(this.getAmountLeft(this.cartTotal));
        if (this.progressBar) {
          this.progressBar.setProgress(this.barPercentage(this.cartTotal));
        }

        const messageElement = this.querySelector('p');
        if (messageElement) {
          messageElement.innerHTML = this.compiledMessage;
        }

        // Update button visibility based on gift status
        this.updateButtonVisibility();
      }

      updateButtonVisibility() {
        // Show/hide the re-add button based on whether gift is in cart
        const reAddBtn = this.querySelector('.free-gift__re-add-btn');
        if (reAddBtn) {
          if (this.hasGift || this.cartTotal < this.freeGiftThreshold) {
            // Hide button if gift is in cart or user is not eligible
            reAddBtn.style.display = 'none';
          } else {
            // Show button if gift is NOT in cart and user IS eligible
            reAddBtn.style.display = 'inline-block';
          }
        }
      }

      barPercentage(cartTotal) {
        return ((cartTotal / this.freeGiftThreshold) * 100).toFixed(2);
      }

      updateFreeGift(event = undefined) {
        if (event && event.detail && event.detail.cartTotal) {
          this.cartTotal = event.detail.cartTotal;
        } else {
          const totalsElement = document.querySelector('.cart__footer .totals__total-value');
          this.cartTotal = Number(totalsElement.dataset.totalValue);
        }
        this.updateContent();
      }

      updateOtherFreeGiftElements() {
        const otherElements = document.querySelectorAll('free-gift');
        otherElements.forEach((element) => {
          if (element !== this) {
            element.setAttribute('data-cart-total', this.cartTotal);
          }
        });
      }

      handleReAddGift() {
        // Show adding message immediately
        this.showAddingMessage();

        // Disable button to prevent double-clicks
        const reAddBtn = this.querySelector('.free-gift__re-add-btn');
        if (reAddBtn) {
          reAddBtn.disabled = true;
          reAddBtn.style.opacity = '0.5';
        }

        // Clear the manual removal block from CartDrawer
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          // Clear the manual removal flag
          const manualRemovalKey = `freeGift_manualRemoval_${this.dataset.freeGiftProductVariantId}`;
          sessionStorage.removeItem(manualRemovalKey);

          // Trigger a cart eligibility check
          // Dispatch a custom event that CartDrawer can listen to
          window.dispatchEvent(
            new CustomEvent('freeGiftReAddRequested', {
              detail: { variantId: this.dataset.freeGiftProductVariantId },
            }),
          );
        }

        // Re-enable button after a delay (in case something goes wrong)
        setTimeout(() => {
          if (reAddBtn) {
            reAddBtn.disabled = false;
            reAddBtn.style.opacity = '1';
          }
        }, 3000);
      }

      updateGiftStatus(hasGift) {
        this.hasGift = hasGift;
        this.updateContent();
      }

      showAddingMessage() {
        // Update the message to show "Adding free gift..." from translations
        const messageElement = this.querySelector('p');
        if (messageElement) {
          this.compiledMessage = this.messageAddingGift;
          messageElement.innerHTML = this.compiledMessage;
        }
      }

      hideAddingMessage() {
        // Restore the original message
        this.updateContent();
      }
    },
  );
}
